import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Briefcase, User } from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { GlobalRole } from '../../types';
import { getRoleHomePath, requiresOrganizationOnboarding } from '../../lib/onboarding';
import KolMyGlassLogo from '../../components/KolMyGlassLogo';

type PendingInfo = { email: string; fullName: string | null };

const ROLE_OPTIONS: Array<{
  id: GlobalRole;
  title: string;
  description: string;
  icon: typeof Building2;
}> = [
  {
    id: 'AGENCY',
    title: 'Agency',
    description:
      'You represent brands, run campaigns, manage KOL relationships, and coordinate deliverables end to end.',
    icon: Briefcase,
  },
  {
    id: 'BRAND',
    title: 'Brand',
    description:
      'You are the advertiser or in-house team: approve talent, review scripts, and sign off on content for your brand.',
    icon: Building2,
  },
  {
    id: 'KOL',
    title: 'KOL / Creator',
    description:
      'You are a creator or influencer: receive invitations, collaborate on briefs, and submit content for campaigns.',
    icon: User,
  },
];

export default function SocialSignupCompletePage() {
  const navigate = useNavigate();
  const { completeSocialSignup } = useAuthStore();
  const [pending, setPending] = useState<PendingInfo | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState<GlobalRole | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ email: string; fullName: string | null }>('/auth/social-signup/pending');
        if (cancelled) return;
        setPending({ email: data.email, fullName: data.fullName ?? null });
        setLoadState('ready');
      } catch {
        await useAuthStore.getState().fetchUser();
        if (cancelled) return;
        const u = useAuthStore.getState().user;
        if (u) {
          navigate(
            requiresOrganizationOnboarding(u) ? '/onboarding/organization' : getRoleHomePath(u),
            { replace: true }
          );
          return;
        }
        setLoadState('error');
        setError('This sign-up link expired or was already completed. Try signing in with Google again from the login page.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) {
      setError('Choose how you will use myKOL to continue.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const user = await completeSocialSignup(selectedRole);
      const base = (
        user.fullName ||
        pending?.fullName ||
        user.email?.split('@')[0] ||
        ''
      ).trim();
      if (base && (selectedRole === 'AGENCY' || selectedRole === 'BRAND')) {
        const suffix = selectedRole === 'AGENCY' ? ' Agency' : ' Brand';
        localStorage.setItem('onboarding:draftOrgName', `${base}${suffix}`);
      }
      if (requiresOrganizationOnboarding(user)) {
        navigate('/onboarding/organization', { replace: true });
      } else {
        navigate(getRoleHomePath(user), { replace: true });
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(msg || 'Could not complete sign-up.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayEmail = pending?.email ?? '';

  return (
    <div className="theme-agency relative min-h-screen w-full overflow-x-hidden px-4 py-10 text-glass-text login-glass-bg">
      <div className="relative z-10 mx-auto flex w-full max-w-lg flex-col items-center gap-8 pb-16">
        <div className="w-full animate-fade-in">
          <div className="glass-card p-8 shadow-xl">
            <div className="mb-8 flex flex-col items-center text-center">
              <Link
                to="/"
                className="mb-3 outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-rgb)/0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900"
              >
                <KolMyGlassLogo variant="hero" heroScale={0.55} />
              </Link>
              <h1 className="text-xl font-semibold tracking-tight text-glass-text">
                Finish your myKOL account
              </h1>
              <p className="mt-2 text-sm text-glass-text-2">
                You signed in with Google as{' '}
                <span className="font-medium text-glass-text">{displayEmail || 'your Google account'}</span>. Tell us how
                you will use the platform—then we will continue into setup.
              </p>
            </div>

            {loadState === 'loading' ? (
              <p className="text-center text-sm text-glass-text-2">Loading…</p>
            ) : loadState === 'error' ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-glass-text-2">{error}</p>
                <Link to="/login" className="font-medium text-accent2 underline-offset-2 hover:underline">
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleContinue} className="space-y-6">
                {error ? (
                  <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200">
                    {error}
                  </div>
                ) : null}

                <div className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-glass-text-3">I am joining as…</p>
                  <div className="grid gap-3">
                    {ROLE_OPTIONS.map(({ id, title, description, icon: Icon }) => {
                      const sel = selectedRole === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            setSelectedRole(id);
                            setError('');
                          }}
                          className={`flex gap-4 rounded-xl border px-4 py-3 text-left transition-colors ${
                            sel
                              ? 'border-accent2/70 bg-accent2/10 ring-2 ring-accent2/35'
                              : 'border-glass bg-glass-2/50 hover:border-glass-text-3/40'
                          }`}
                        >
                          <Icon className="mt-0.5 h-6 w-6 shrink-0 text-accent2" aria-hidden />
                          <span>
                            <span className="block font-semibold text-glass-text">{title}</span>
                            <span className="mt-1 block text-sm text-glass-text-2">{description}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !selectedRole}
                  className="btn-accent w-full transform py-3 font-medium shadow-lg transition-all duration-200 ease-in-out hover:-translate-y-0.5 focus:outline-none disabled:transform-none disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? 'Creating your profile…' : 'Continue'}
                </button>

                <p className="text-center text-xs text-glass-text-3">
                  Agency and brand accounts create an organization next. KOL accounts go straight to the creator
                  dashboard to complete their profile.
                </p>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-glass-text-3">
          Wrong account?{' '}
          <Link to="/login" className="font-medium text-accent2 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
